import gql from 'graphql-tag'
import { parsePhoneNumberFromString } from 'libphonenumber-js/min'
import { upperFirst } from 'lodash-es'
import { load } from 'recaptcha-v3'
import * as yup from 'yup'

import { useI18n } from '#imports'

/**
 * Helpers for rendering and submitting Formie-managed forms via Builder.vue.
 *
 * Responsibilities:
 *  - Map Formie field typeNames to Vue input components (fieldMap / componentMatcher)
 *  - Build a Yup validation schema from the Formie field definitions (createFormSchema)
 *  - Generate the GraphQL submission mutation at runtime (createFormQuery)
 *  - Handle reCAPTCHA token loading and execution
 */
export default () => {
  const { t } = useI18n()

  /**
   * Maps Formie typeName → local input component name.
   *
   * Field_Entries: treated as a select because its options are entry IDs.
   * Builder.vue may override the rendered component for store-locator fields
   * (e.g. Field_SingleLineText → InputSelectNative) via getFieldComponent().
   */
  const fieldMap = {
    Filed_Hidden: 'InputText',
    Field_Name: 'InputText',
    Field_Email: 'InputText',
    Field_Phone: 'InputPhone',
    Field_Dropdown: 'InputSelectNative',
    Field_Entries: 'InputSelectNative',
    Field_SingleLineText: 'InputText',
    Field_MultiLineText: 'InputTextarea',
    Field_Agree: 'InputCheckbox',
    Field_FileUpload: 'InputFile',
    Field_Date: 'InputDate'
  }

  const componentMatcher = name => fieldMap[name]

  const applyTypeOf = (rule, type) => {
    switch (type) {
      case 'string':
        rule = rule.string()
        break

      case '[fileuploadinput]':
        rule = rule.mixed()
        break

      default:
        rule = rule.string()
        break
    }
    return rule
  }

  const applyRequired = (rule, required) => {
    return required ? rule.required() : rule
  }

  const supportedFileFormats = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'image/jpeg',
    'image/png',
    'application/pdf',
    'application/zip',
    'application/x-zip-compressed'
  ]

  const maxFileSize = 20 * 1024 * 1024

  const applySpecificValidation = (rule, field) => {
    switch (field.typeName) {
      case 'Field_Email':
        rule = rule.email()
        break
      case 'Field_Input':
        rule = rule.mixed()
          .test('fileSize', t('forms.errors.fileSize'), file => {
            return !file || (file && file.size <= maxFileSize.value)
          })
          .test('fileType', t('forms.errors.fileType'), file => {
            return !file || (file && supportedFileFormats.value.includes(file.type))
          })
        break
      case 'Field_Phone': {
        rule = rule.test(
          'phone-valid',
          t('forms.errors.phone'),
          value => {
            if (value == '' || !value) {
              return true
            } else {
              const number = parsePhoneNumberFromString(value, !field.countryEnabled ? 'IT' : null)
              return number?.isPossible() ?? false
            }
          }
        )
        break
      }
      default:
        break
    }
    return rule
  }

  const applyLabel = (rule, label) => {
    return rule.label(label)
  }

  /**
   * Obbligatorieta' effettiva di un campo.
   *
   * I campi Date di Formie sono contenitori: il plugin azzera sempre il `required` del campo
   * esterno (fields/Date.php, "Prevent a required state set at the top-level field") e tiene
   * l'obbligatorieta' nei sotto-campi. Leggendo solo il flag esterno il frontend mostrava un
   * campo facoltativo che il server poi rifiutava, senza asterisco e senza errore in pagina.
   */
  const isFieldRequired = field => {
    return !!(field?.required || field?.subFields?.some(sub => sub.required))
  }

  const chainRules = data => {
    // inputTypeName can be null for Field_Entries and other non-standard types;
    // default to 'string' so applyTypeOf doesn't throw.
    const inputType = data.inputTypeName ? data.inputTypeName.toLowerCase() : 'string'
    let rule = applyTypeOf(yup, inputType)
    rule = applyRequired(rule, isFieldRequired(data))
    rule = applySpecificValidation(rule, data)
    rule = applyLabel(rule, data.label)
    return rule
  }

  const createFormSchema = formdata => {
    let schema = formdata.reduce((acc, val) => ([ ...acc, ...val.fields ]), [])
    schema = schema.reduce((acc, val) => ({
      ...acc,
      [val.name]: chainRules(val)
    }), {})

    return schema
  }

  /**
   * Builds the Formie submission GraphQL mutation at runtime.
   *
   * @param {Object} formSchema        - Yup schema keyed by field handle
   * @param {string} mutationName      - Formie submissionMutationName
   * @param {boolean} hasRecaptcha     - Whether to inject reCAPTCHA variable
   * @param {Object} fieldTypeNameMap  - Map of handle → Formie typeName, used to override
   *                                     GQL scalar types (e.g. Field_Entries → [Int]).
   *                                     Without this, Field_Entries would fall back to the
   *                                     Yup schema type ('String') and Formie would reject it.
   */
  const createFormQuery = (formSchema, mutationName, hasRecaptcha, fieldTypeNameMap = {}, timeOnlyFields = new Set()) => {
    const resolveGqlType = k => {
      if (k.includes('file') || k.includes('upload') || k.includes('attached')) return '[FileUploadInput]'
      if (k?.toLowerCase()?.includes('birthday') || k?.toLowerCase()?.includes('date') || fieldTypeNameMap[k] === 'Field_Date') return 'DateTime'
      // Field_Entries expects an array of entry IDs; Formie GraphQL schema types it as [Int]
      if (fieldTypeNameMap[k] === 'Field_Entries') return '[Int]'
      return upperFirst(formSchema[k].type) ?? ''
    }

    const _types = Object.keys(formSchema).map(k => [ `$${k}`, resolveGqlType(k) ].join(':')).join(',')

    const _vars = Object.keys(formSchema).map(k => [ k, `$${k}` ].join(':')).join('\n')
    let _recaptchaType
    let _recaptcha

    if (hasRecaptcha) {
      _recaptchaType = ', $recaptchaCaptcha: FormieCaptchaInput'
      _recaptcha = 'recaptchaCaptcha: $recaptchaCaptcha'
    }

    const query = gql`
      mutation ${mutationName} (
        ${_types}
        ${_recaptchaType || ''}
      ) {
        ${mutationName} (
          ${_vars}
          ${_recaptcha || ''}
        ) {
          id
          isSpam
          spamReason
          status
        }
      }
    `

    return query
  }

  const recaptchaInstance = ref(null)

  const loadRecaptcha = async sitekey => {
    if (import.meta.client && !recaptchaInstance.value) {
      recaptchaInstance.value = await load(sitekey, { autoHideBadge: false })
    }
  }

  const executeRecaptcha = async (action, sitekey) => {
    await loadRecaptcha(sitekey)
    const token = await recaptchaInstance.value.execute(action.name)

    const headerOptions = { headers: { 'google-recaptcha-token': token } }
    return { token, headerOptions }
  }

  return {
    supportedFileFormats,
    componentMatcher,
    createFormSchema,
    createFormQuery,
    isFieldRequired,
    loadRecaptcha,
    executeRecaptcha
  }
}
